import { useState } from 'react';
import { useApi } from '../hooks/useApi';
import StatCard from '../components/StatCard';
import './AnalyticsPage.css';

interface PlatformStat {
  platform: string;
  shop_name: string | null;
  task_count: number;
  total_salary: number;
}

interface WorkerStat {
  worker_type: string;
  task_count: number;
  total_salary: number;
}

interface DailyTrend {
  date: string;
  task_count: number;
  total_salary: number;
}

interface AnalyticsSummary {
  shops: Array<{ id: number; platform: string; shop_name: string | null }>;
  platformStats: PlatformStat[];
  workerStats: WorkerStat[];
  dailyTrend: DailyTrend[];
}

interface Report {
  id: number;
  category: string;
  title: string;
  content: string;
  created_at: string;
}

const WORKER_LABELS: Record<string, string> = {
  OrderWorker: '주문 처리',
  ProductWorker: '상품 관리',
  CustomerWorker: '고객 응대',
  PromotionWorker: '프로모션',
  AnalyticsWorker: '데이터 분석',
  SyncWorker: '동기화',
};

const PLATFORM_LABELS: Record<string, string> = {
  cafe24: '카페24',
  imweb: '아임웹',
  nhn_godo: 'NHN 고도몰',
  nhn_shopby: 'NHN 샵바이',
};

function formatSalary(amount: number): string {
  if (amount >= 10000) {
    return `${(amount / 10000).toFixed(1)}만`;
  }
  return amount.toLocaleString();
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('ko-KR', {
    month: 'short',
    day: 'numeric',
  });
}

export default function AnalyticsPage() {
  const { data: summary, isLoading: summaryLoading } = useApi<AnalyticsSummary>('/analytics/summary');
  const [selectedShopId, setSelectedShopId] = useState<number | null>(null);
  const [reportType, setReportType] = useState<'daily' | 'weekly'>('daily');

  const shopId = selectedShopId ?? summary?.shops[0]?.id;
  const { data: reportsData, isLoading: reportsLoading } = useApi<{ reports: Report[] }>(
    shopId ? `/analytics/reports?shopId=${shopId}&type=${reportType}&limit=10` : null
  );

  const totalTasks = summary?.workerStats.reduce((sum, w) => sum + w.task_count, 0) ?? 0;
  const totalSalary = summary?.workerStats.reduce((sum, w) => sum + w.total_salary, 0) ?? 0;
  const platformCount = summary?.platformStats.length ?? 0;

  return (
    <div className="analytics">
      <div className="analytics__header">
        <h2 className="analytics__title">운영 분석</h2>
        <span className="analytics__period">최근 30일 기준</span>
      </div>

      {/* 요약 카드 */}
      <div className="analytics__stats-grid">
        <StatCard
          title="총 처리 건수"
          value={totalTasks}
          unit="건"
          icon="📊"
          isLoading={summaryLoading}
        />
        <StatCard
          title="총 급여 소모"
          value={totalSalary}
          unit="원"
          icon="💰"
          isLoading={summaryLoading}
        />
        <StatCard
          title="연결 플랫폼"
          value={platformCount}
          unit="개"
          icon="🔗"
          isLoading={summaryLoading}
        />
        <StatCard
          title="활동 워커"
          value={summary?.workerStats.length ?? 0}
          unit="명"
          icon="🤖"
          isLoading={summaryLoading}
        />
      </div>

      {/* 플랫폼별 통계 */}
      <section className="analytics__section">
        <h3 className="analytics__section-title">플랫폼별 현황</h3>
        <div className="analytics__table-wrap">
          <table className="analytics__table">
            <thead>
              <tr>
                <th>플랫폼</th>
                <th>쇼핑몰</th>
                <th>처리 건수</th>
                <th>급여 소모</th>
              </tr>
            </thead>
            <tbody>
              {summaryLoading ? (
                <tr><td colSpan={4} className="analytics__empty">로딩 중...</td></tr>
              ) : (summary?.platformStats ?? []).length === 0 ? (
                <tr><td colSpan={4} className="analytics__empty">데이터가 없습니다.</td></tr>
              ) : (
                summary?.platformStats.map((stat, i) => (
                  <tr key={i}>
                    <td>{PLATFORM_LABELS[stat.platform] ?? stat.platform}</td>
                    <td>{stat.shop_name ?? '-'}</td>
                    <td className="text-right">{stat.task_count.toLocaleString()}건</td>
                    <td className="text-right">{formatSalary(stat.total_salary)}원</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* 워커별 통계 */}
      <section className="analytics__section">
        <h3 className="analytics__section-title">AI 팀원별 활동</h3>
        <div className="analytics__worker-grid">
          {summaryLoading ? (
            <div className="analytics__empty">로딩 중...</div>
          ) : (
            summary?.workerStats.map((worker) => (
              <div key={worker.worker_type} className="analytics__worker-card">
                <div className="analytics__worker-name">
                  {WORKER_LABELS[worker.worker_type] ?? worker.worker_type}
                </div>
                <div className="analytics__worker-count">{worker.task_count.toLocaleString()}건</div>
                <div className="analytics__worker-salary">{formatSalary(worker.total_salary)}원</div>
              </div>
            ))
          )}
        </div>
      </section>

      {/* 일별 추이 */}
      {summary?.dailyTrend && summary.dailyTrend.length > 0 && (
        <section className="analytics__section">
          <h3 className="analytics__section-title">일별 처리 추이</h3>
          <div className="analytics__trend">
            {(() => {
              const maxCount = Math.max(...summary.dailyTrend.map(d => d.task_count), 1);
              return summary.dailyTrend.map((day) => {
              const heightPct = Math.max((day.task_count / maxCount) * 100, 4);
              return (
                <div key={day.date} className="analytics__trend-bar-wrap">
                  <div
                    className="analytics__trend-bar"
                    style={{ height: `${heightPct}%` }}
                    title={`${day.date}: ${day.task_count}건, ${formatSalary(day.total_salary)}원`}
                  />
                  <span className="analytics__trend-label">{formatDate(day.date)}</span>
                </div>
              );
            });
            })()}
          </div>
        </section>
      )}

      {/* 리포트 목록 */}
      <section className="analytics__section">
        <div className="analytics__section-header">
          <h3 className="analytics__section-title">분석 리포트</h3>
          <div className="analytics__report-filters">
            {summary?.shops && summary.shops.length > 1 && (
              <select
                className="analytics__select"
                value={shopId ?? ''}
                onChange={(e) => setSelectedShopId(Number(e.target.value))}
              >
                {summary.shops.map((shop) => (
                  <option key={shop.id} value={shop.id}>
                    {shop.shop_name ?? shop.platform}
                  </option>
                ))}
              </select>
            )}
            <div className="analytics__tabs">
              <button
                className={`analytics__tab ${reportType === 'daily' ? 'active' : ''}`}
                onClick={() => setReportType('daily')}
              >
                일일
              </button>
              <button
                className={`analytics__tab ${reportType === 'weekly' ? 'active' : ''}`}
                onClick={() => setReportType('weekly')}
              >
                주간
              </button>
            </div>
          </div>
        </div>

        <div className="analytics__reports">
          {reportsLoading ? (
            <div className="analytics__empty">리포트 로딩 중...</div>
          ) : !reportsData?.reports || reportsData.reports.length === 0 ? (
            <div className="analytics__empty">
              아직 생성된 리포트가 없습니다.
              <br />
              <span className="analytics__empty-sub">리포트는 매일 오전 6시(일일), 매주 월요일 오전 9시(주간) 자동 생성됩니다.</span>
            </div>
          ) : (
            reportsData.reports.map((report) => (
              <ReportCard key={report.id} report={report} />
            ))
          )}
        </div>
      </section>
    </div>
  );
}

function ReportCard({ report }: { report: Report }) {
  const [expanded, setExpanded] = useState(false);

  let parsed: Record<string, unknown> = {};
  try {
    parsed = JSON.parse(report.content);
  } catch {
    // content가 JSON이 아닐 수 있음
  }

  const isWeekly = report.category === 'weekly_report';
  const aiAnalysis = parsed['ai_analysis'] as string | undefined;

  return (
    <div className={`analytics__report-card ${isWeekly ? 'weekly' : 'daily'}`}>
      <div className="analytics__report-header" onClick={() => setExpanded(!expanded)}>
        <div>
          <span className={`badge ${isWeekly ? 'badge-primary' : 'badge-secondary'}`}>
            {isWeekly ? '주간' : '일일'}
          </span>
          <span className="analytics__report-title">{report.title}</span>
        </div>
        <span className="analytics__report-date">
          {new Date(report.created_at).toLocaleDateString('ko-KR')}
        </span>
      </div>
      {expanded && (
        <div className="analytics__report-body">
          {aiAnalysis ? (
            <pre className="analytics__report-ai">{aiAnalysis}</pre>
          ) : (
            <pre className="analytics__report-raw">{JSON.stringify(parsed, null, 2)}</pre>
          )}
        </div>
      )}
    </div>
  );
}
